import { useState, useEffect } from 'react'
import { Routes, Route, Link, useLocation } from 'react-router-dom'
import { UserAPI } from '@certi/shared/api/user/UserAPI'
import type { UserProfileResponse, UserStatisticsResponse } from '@certi/shared/types'
import { useAuth } from '@/hooks/useAuth'
import SettingsSection from '@/components/profile/SettingsSection'
import BidsSection from '@/components/profile/BidsSection'
import OrdersSection from '@/components/profile/OrdersSection'
import CardsSection from '@/components/profile/CardsSection'
import AddressesSection from '@/components/profile/AddressesSection'
import VerificationProgress from '@/components/profile/VerificationProgress'

export default function ProfilePage() {
  const { user, session } = useAuth()
  const location = useLocation()
  const [profile, setProfile] = useState<UserProfileResponse | null>(null)
  const [statistics, setStatistics] = useState<UserStatisticsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user?.id || !session?.accessToken) return

    const fetchProfileAndStatistics = async () => {
      try {
        setLoading(true)
        setError(null)

        // Use mock data for dev login
        if (session.accessToken === 'fake-access-token') {
          const mockProfile: UserProfileResponse = {
            id: user.id,
            email: user.email,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            phone: undefined,
            status: user.status,
          }
          setProfile(mockProfile)
          setStatistics({ itemsWon: 0, totalBidsPlaced: 0, itemsBidOn: 0 })
        } else {
          const [profileResponse, statsResponse] = await Promise.all([
            UserAPI.getUserProfile(user.id, session.accessToken),
            UserAPI.getUserStatistics(user.id, session.accessToken)
          ])
          setProfile(profileResponse)
          setStatistics(statsResponse)
        }
      } catch (err) {
        console.error('Failed to fetch profile:', err)
        setError(err instanceof Error ? err.message : 'Failed to load profile')
      } finally {
        setLoading(false)
      }
    }

    fetchProfileAndStatistics()
  }, [user?.id, session?.accessToken])

  const handleProfileUpdate = (updatedProfile: UserProfileResponse) => {
    setProfile(updatedProfile)
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-8"></div>
          <div className="space-y-6">
            <div className="h-6 bg-gray-200 rounded w-1/4"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !profile) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Error Loading Profile
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{user?.firstName || 'User'}</h1>
      </div>

      {/* Layout: Sidebar + Content */}
      <div className="flex gap-8">
        {/* Vertical Navigation Sidebar */}
        <nav className="w-48 flex-shrink-0">
          <div className="space-y-1">
            <Link
              to="/profile"
              className={`block w-full text-left px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                location.pathname === '/profile'
                  ? 'bg-brand-primary text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Profile
            </Link>
            <Link
              to="/profile/bids"
              className={`block w-full text-left px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                location.pathname === '/profile/bids'
                  ? 'bg-brand-primary text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Bids
            </Link>
            <Link
              to="/profile/orders"
              className={`block w-full text-left px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                location.pathname === '/profile/orders'
                  ? 'bg-brand-primary text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Orders
            </Link>
            <Link
              to="/profile/cards"
              className={`block w-full text-left px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                location.pathname === '/profile/cards'
                  ? 'bg-brand-primary text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Cards
            </Link>
            <Link
              to="/profile/settings"
              className={`block w-full text-left px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                location.pathname === '/profile/settings'
                  ? 'bg-brand-primary text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Settings
            </Link>
          </div>
        </nav>

        {/* Tab Content */}
        <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <Routes>
            <Route
              index
              element={
                <div>
                  {/* Verification Progress */}
                  <div className="mb-6">
                    <VerificationProgress />
                  </div>

                  {/* All Time Statistics */}
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">All Time Statistics</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gray-50 rounded-lg p-6 text-center">
                      <div className="text-4xl font-bold text-brand-primary mb-2">
                        {statistics?.itemsWon ?? 0}
                      </div>
                      <div className="text-sm text-gray-600">Items Won</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-6 text-center">
                      <div className="text-4xl font-bold text-brand-primary mb-2">
                        {statistics?.totalBidsPlaced ?? 0}
                      </div>
                      <div className="text-sm text-gray-600">Total Bids Placed</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-6 text-center">
                      <div className="text-4xl font-bold text-brand-primary mb-2">
                        {statistics?.itemsBidOn ?? 0}
                      </div>
                      <div className="text-sm text-gray-600">Items Bid On</div>
                    </div>
                  </div>
                </div>
              }
            />
            <Route
              path="bids"
              element={<BidsSection />}
            />
            <Route
              path="orders"
              element={<OrdersSection />}
            />
            <Route
              path="cards"
              element={<CardsSection />}
            />
            <Route
              path="settings"
              element={
                <SettingsSection
                  profile={profile}
                  onUpdate={handleProfileUpdate}
                />
              }
            />
            <Route
              path="addresses"
              element={<AddressesSection />}
            />
          </Routes>
        </div>
      </div>
    </div>
  )
}